import React, { cloneElement, forwardRef, ReactElement, ReactNode, useCallback, useMemo, useRef } from 'react'
import {
  GestureResponderEvent,
  NativeSyntheticEvent,
  Pressable,
  PressableProps,
  PressableStateCallbackType,
  TargetedEvent,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native'

import { noop } from 'lodash-es'
import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'
import { useFocusVisible } from 'use-focus-visible'

import { getAuroraButtonStyles } from './auroraButtonStyles'
import { getButtonStyles } from './buttonStyles'
import { AccessibilityRolesNative, IS_WEB } from '../../constants'
import { BodyTextSize, getFontStyles, TextAlign } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { BaseSkeleton } from '../baseSkeleton/BaseSkeleton'
import { BodyText, Size as TextSize } from '../bodyText/BodyText'

export enum ButtonType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
  TEXT = 'text',
  WORDCLOUD = 'wordCloud',
  PRIMARY_ICON = 'primary_icon',
  SECONDARY_ICON = 'secondary_icon',

  /** @deprecated Use PRIMARY_ICON instead */
  ICON = 'icon',
  TERTIARY_ICON = 'tertiary_icon',
  TERTIARY_HEADER = 'tertiary_header',
  SECONDARY_TERTIARY_ICON = 'secondary_tertiary_icon',
  CAROUSEL = 'carousel',
  WHITE_ICON = 'white_icon',
  TEAL_ICON = 'teal_icon',
  SURVEY_ICON = 'survey_icon',
}

export enum ButtonSize {
  DEFAULT = 'default',
  SMALL = 'small',
  EXTRA_SMALL = 'extraSmall',
  LARGE = 'large',
  UNSIZED = 'unsized',
}

export enum ButtonModifier {
  DEFAULT = 'default',
  INVERSE = 'inverse',
  UNSTYLED = 'unstyled',
  BLACK = 'black',
  PERIWINKLE = 'periwinkle',
  FILLED = 'filled',
  GOLD = 'gold',
  OATMEAL = 'oatmeal',
  ROUND_ARROW = 'roundArrow',
}

export interface BaseButtonProps
  extends Omit<PressableProps, 'accessibilityLabelledBy' | 'accessibilityRole' | 'accessibilityLabel'> {
  onPress: (event: GestureResponderEvent) => void
  text?: React.ReactNode
  buttonType?: ButtonType
  leftIcon?: ReactElement | null
  rightIcon?: ReactElement | null
  iconAtEnd?: boolean
  testID?: string
  disabled?: boolean
  loading?: boolean
  skeleton?: boolean
  style?: ViewStyle
  size?: ButtonSize
  fullWidth?: boolean
  modifier?: ButtonModifier
  iconColor?: string
  accessibilityLabel?: string | ReactNode
  accessibilityLabelledBy?: string | ReactNode
  accessibilityRole?: AccessibilityRolesNative
  suppressAccessibilitySelected?: boolean
  selected?: boolean
  textAlign?: TextAlign
  customTextColor?: string
  customTextSize?: BodyTextSize
  underlineText?: boolean
  focusable?: boolean
  focusBorderStyle?: CSSObject
  disableCenteredContent?: boolean
  flexDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
  leftIconFilled?: boolean
  textStyle?: TextStyle
  contentContainerStyle?: CSSObject
}

type GetButtonStylesArgs = {
  buttonType: ButtonType
  modifier?: string
  size?: ButtonSize
  focused?: boolean
  hovered?: boolean
  pressed?: boolean
  disabled?: boolean
  selected?: boolean
  loading?: boolean
}

const iconButtons = [
  ButtonType.ICON,
  ButtonType.PRIMARY_ICON,
  ButtonType.TERTIARY_ICON,
  ButtonType.SECONDARY_ICON,
  ButtonType.SECONDARY_TERTIARY_ICON,
  ButtonType.CAROUSEL,
  ButtonType.WHITE_ICON,
  ButtonType.TEAL_ICON,
  ButtonType.SURVEY_ICON,
]

const Container = styled(Pressable)<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  position: 'relative',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '100px',
  alignSelf: 'flex-start',
  ...(IS_WEB && { width: 'fit-content' }),
  ...(fullWidth && (IS_WEB ? { width: 'auto', alignSelf: 'auto' } : { alignSelf: 'auto' })),
}))

const FocusBorder = styled.View<{ theme: ThemeType; customStyle?: CSSObject }>(({ theme, customStyle }) => ({
  position: 'absolute',
  top: '-6px',
  right: '-6px',
  bottom: '-6px',
  left: '-6px',
  backgroundColor: 'transparent',
  border: '2px',
  borderColor: theme.colors.borderFocus,
  borderRadius: '100px',
  ...customStyle,
}))

const TealIconContainer = styled.View<{
  theme: ThemeType
  backgroundColor?: string
}>(({ theme, backgroundColor }) => ({
  alignItems: 'center',
  backgroundColor: backgroundColor ?? theme.colors.backgroundHighlightPrimary,
  borderRadius: theme.spacing['32px'],
  height: theme.spacing['64px'],
  justifyContent: 'center',
  width: theme.spacing['64px'],
}))

const CarouselContainer = styled.View<{
  theme: ThemeType
  backgroundColor?: string
  borderColor?: string
}>(({ theme, backgroundColor, borderColor }) => ({
  alignItems: 'center',
  backgroundColor: backgroundColor ?? theme.colors.backgroundPrimary,
  borderColor: borderColor ?? theme.colors.backgroundTransparent,
  borderRadius: theme.spacing['24px'],
  borderWidth: '1.5px',
  height: theme.spacing['40px'],
  justifyContent: 'center',
  width: theme.spacing['40px'],
}))

const LeftIconContainer = styled.View<{ noMargin?: boolean; iconAtEnd?: boolean }>(({ noMargin, iconAtEnd }) => ({
  ...(iconAtEnd
    ? { position: 'absolute', left: '0' }
    : { justifyContent: 'center', marginRight: noMargin ? '0' : '8px' }),
}))

const RightIconContainer = styled.View<{ iconAtEnd?: boolean }>(({ iconAtEnd }) => ({
  ...(iconAtEnd ? { position: 'absolute', right: '0' } : { justifyContent: 'center', marginLeft: '8px' }),
}))

const ContentContainer = styled.View<{
  hide?: boolean
  iconAtEnd?: boolean
  disableCenteredContent?: boolean
  flexDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
  customStyle?: CSSObject
}>(({ hide, iconAtEnd, disableCenteredContent, flexDirection = 'row', customStyle }) => ({
  flexDirection,
  alignItems: 'center',
  ...(iconAtEnd && { width: '100%' }),
  ...(hide && { opacity: 0 }),
  ...(!disableCenteredContent && { justifyContent: 'center' }),
  ...customStyle,
}))

const LoadingIndicator = styled.ActivityIndicator({
  position: 'absolute',
  left: '0px',
  right: '0px',
})

export const BaseButton = forwardRef<React.ElementRef<typeof View>, BaseButtonProps>(
  (
    {
      onPress,
      text,
      leftIcon,
      rightIcon,
      iconAtEnd,
      buttonType = ButtonType.PRIMARY,
      modifier,
      testID = 'Button',
      disabled,
      loading,
      skeleton = false,
      selected,
      style,
      size = ButtonSize.DEFAULT,
      fullWidth,
      iconColor,
      accessibilityLabel,
      accessibilityLabelledBy,
      accessibilityRole,
      suppressAccessibilitySelected = false,
      onFocus = noop,
      onBlur = noop,
      textAlign = 'center',
      customTextColor,
      customTextSize,
      underlineText = false,
      focusable = true,
      focusBorderStyle,
      disableCenteredContent = false,
      flexDirection = 'row',
      leftIconFilled,
      textStyle,
      contentContainerStyle,
      ...rest
    },
    ref,
  ) => {
    const { focusVisible, onBlur: fvBlur, onFocus: fvFocus } = useFocusVisible()
    const pressableRef = useRef<React.ElementRef<typeof View>>(null)
    const { colors, fontFamilyMetadata, isAuroraTheme } = useTheme()
    const buttonStyles = useMemo(
      () => (isAuroraTheme ? getAuroraButtonStyles(colors) : getButtonStyles(colors)),
      [colors, isAuroraTheme],
    )
    const fontStyles = getFontStyles(colors, fontFamilyMetadata)

    const focus = (event: NativeSyntheticEvent<TargetedEvent>) => {
      fvFocus()
      onFocus?.(event)
    }

    const blur = (event: NativeSyntheticEvent<TargetedEvent>) => {
      fvBlur()
      onBlur?.(event)
    }

    const getButtonStylesFromState = ({
      buttonType,
      modifier = 'default',
      focused,
      hovered,
      pressed,
      disabled,
      selected,
      loading,
    }: GetButtonStylesArgs) => {
      let state = 'active'
      if (hovered) state = 'hovered'
      if (pressed) state = 'pressed'
      if (disabled) state = 'inactive'
      if (selected) state = 'selected'
      if (selected && disabled) state = 'selected-inactive'
      if (loading) state = 'loading'
      const styles = {
        ...buttonStyles[buttonType][modifier].base,
        ...buttonStyles[buttonType][modifier].sizes[size],
        ...(focused && buttonStyles[buttonType][modifier].focused),
        ...buttonStyles[buttonType][modifier].states[state],
      }
      return styles
    }
    const accessibilityAttr = accessibilityLabelledBy ? { accessibilityLabelledBy } : { accessibilityLabel }

    const getLeftIcon = useCallback(
      ({
        backgroundColor,
        borderColor,
        textColor,
      }: {
        backgroundColor?: string
        borderColor?: string
        textColor?: string
      }) => {
        if (disableCenteredContent && leftIcon && rightIcon && iconAtEnd) {
          return <LeftIconContainer>{cloneElement(leftIcon, { fillColor: iconColor || textColor })}</LeftIconContainer>
        } else if (leftIcon && buttonType === ButtonType.TEAL_ICON) {
          return (
            <TealIconContainer backgroundColor={backgroundColor}>
              {cloneElement(leftIcon, { fillColor: textColor })}
            </TealIconContainer>
          )
        } else if (leftIcon && buttonType === ButtonType.CAROUSEL) {
          return (
            <CarouselContainer backgroundColor={backgroundColor} borderColor={borderColor}>
              {cloneElement(leftIcon, { fillColor: iconColor || textColor })}
            </CarouselContainer>
          )
        } else if (leftIcon) {
          return (
            <LeftIconContainer iconAtEnd={iconAtEnd} noMargin={iconButtons.includes(buttonType)}>
              {cloneElement(leftIcon, {
                fillColor: iconColor || textColor,
                ...(leftIconFilled !== undefined && { isFilled: leftIconFilled }),
              })}
            </LeftIconContainer>
          )
        }

        return null
      },
      [buttonType, disableCenteredContent, iconAtEnd, iconColor, leftIcon, rightIcon, leftIconFilled],
    )

    if (skeleton) {
      const buttonStyles = getButtonStylesFromState({
        buttonType,
        selected: false,
        hovered: false,
        pressed: false,
        focused: false,
        disabled: false,
        size,
        modifier,
        loading: false,
      })

      return (
        <BaseSkeleton
          layout={[
            {
              key: 'button',
              width: fullWidth ? '100%' : buttonStyles.minWidth || 100,
              height: buttonStyles.height || 48,
              borderRadius: 100,
            },
          ]}
        />
      )
    }

    return (
      <Container
        ref={ref || pressableRef}
        accessible
        focusable={focusable}
        // ignoring the next line because it looks like styled-components/native AccessibilityRole is different than the 0.68 react-native
        // @ts-ignore next-line
        accessibilityRole={accessibilityRole || AccessibilityRolesNative.BUTTON}
        accessibilitySelected={
          accessibilityRole === AccessibilityRolesNative.TAB && !suppressAccessibilitySelected ? selected : undefined
        }
        aria-checked={
          accessibilityRole === AccessibilityRolesNative.RADIO ||
          accessibilityRole === AccessibilityRolesNative.CHECKBOX ||
          accessibilityRole === AccessibilityRolesNative.SWITCH
            ? selected
              ? true
              : false
            : undefined
        }
        onFocus={focus}
        onBlur={blur}
        onPress={loading || disabled ? () => {} : onPress}
        testID={testID}
        disabled={disabled}
        fullWidth={fullWidth}
        dataSet={{ selected: selected ? 'true' : 'false' }}
        style={({ hovered, pressed }: PressableStateCallbackType) => [
          { outline: 0 },
          getButtonStylesFromState({
            buttonType,
            selected,
            hovered,
            pressed,
            focused: focusVisible,
            disabled,
            size,
            modifier,
            loading,
          }),
          style,
        ]}
        {...accessibilityAttr}
        {...rest}
      >
        {({ hovered, pressed }) => {
          const {
            color: textColor,
            textSize: textSizeFromButtonStyles,
            backgroundColor,
            borderColor,
          } = getButtonStylesFromState({
            buttonType,
            hovered,
            pressed,
            focused: focusVisible,
            disabled,
            size,
            modifier,
            selected,
            loading,
          })
          const textSize = customTextSize || textSizeFromButtonStyles || TextSize.DEFAULT

          return (
            <>
              {/* To do use LoadingIndicator in ui-core-crossplatform instead of this custom one */}
              {loading && <LoadingIndicator color={textColor} accessibilityLabel='Loading indicator' />}
              {focusVisible ? <FocusBorder customStyle={focusBorderStyle} /> : null}
              <ContentContainer
                hide={loading}
                iconAtEnd={iconAtEnd}
                disableCenteredContent={disableCenteredContent}
                flexDirection={flexDirection as 'row' | 'column' | 'row-reverse' | 'column-reverse'}
                customStyle={contentContainerStyle}
              >
                {getLeftIcon({ backgroundColor, borderColor, textColor })}
                {text && (
                  <BodyText
                    text={text}
                    color={customTextColor ?? textColor}
                    textAlign={textAlign}
                    size={textSize}
                    underline={underlineText}
                    style={{ lineHeight: fontStyles.body[textSize].lineHeight, ...textStyle }}
                    selectable={false}
                    maxFontSizeMultiplier={2}
                  />
                )}
                {rightIcon && (
                  <RightIconContainer iconAtEnd={iconAtEnd}>
                    {cloneElement(rightIcon, { fillColor: iconColor || textColor })}
                  </RightIconContainer>
                )}
              </ContentContainer>
            </>
          )
        }}
      </Container>
    )
  },
)
